import React, { useRef, useEffect, useState, ReactNode, RefObject } from 'react'

import Plyr, { Source, SourceInfo, Track } from 'plyr'
import className from 'classnames/bind'

import defaultOptions from 'app/components/video-player/default-options'

import stylesModule from 'app/components/video-player/video-player.module.scss'

type VideoPlayerProps = {
  playButtonIcon: ReactNode
  classNames: string
  placeholder?: boolean
  source?: SourceInfo
  onPlay?: (plyrRef: RefObject<Plyr | null>) => void
  onPause?: (plyrRef: RefObject<Plyr | null>) => void
  onEnd?: (plyrRef: RefObject<Plyr | null>) => void
  onDestroy?: (plyrRef: RefObject<Plyr | null>) => void
  options?: Plyr.Options
  sources?: Source[]
  tracks?: Track[]
  poster?: string
}

const cx = className.bind(stylesModule)

function addLoaded(element?: HTMLElement | null) {
  if (element) element.classList.add(cx('loaded'))
}

const VideoPlayer = (props: VideoPlayerProps) => {
  const {
    source,
    playButtonIcon,
    onPlay,
    onPause,
    onEnd,
    onDestroy,
    options = defaultOptions,
    sources = [],
    tracks = [],
    poster = '',
    classNames = '',
    placeholder = false,
  } = props

  const playerRef = useRef(null)
  const plyrRef = useRef<Plyr | null>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)

  const [isPlayButtonShown, showPlayButton] = useState(true)

  function playVideo() {
    const playPromise = plyrRef.current?.play()

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          showPlayButton(false)
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }

  useEffect(() => {
    if (!playerRef.current) return

    plyrRef.current = new Plyr(playerRef.current, options)

    return () => {
      plyrRef.current?.destroy()

      // need to hardly clear the video src to remove socket and cancel pending requests
      const video = document.querySelector('video')
      if (video) {
        video.src = ''
      }
    }
  }, [options])

  useEffect(() => {
    const videoPlayer = plyrRef.current

    if (!videoPlayer) return

    videoPlayer.stop()

    if (!source) return

    videoPlayer.source = source
    showPlayButton(true)
  }, [source])

  useEffect(() => {
    if (!placeholder || !placeholderRef.current) return
    const img = new Image()

    img.src = poster || (source?.poster ?? '')
    img.onload = () => addLoaded(placeholderRef.current)
    img.onerror = () => addLoaded(placeholderRef.current)
  }, [placeholder, poster, source?.poster])

  useEffect(() => {
    const videoPlayer = plyrRef.current

    videoPlayer?.on('pause', () => {
      showPlayButton(true)
      onPause?.(plyrRef)
    })

    videoPlayer?.on('play', () => {
      showPlayButton(false)
      onPlay?.(plyrRef)
    })

    videoPlayer?.on('ended', () => {
      onEnd?.(plyrRef)
    })

    return () => {
      onDestroy?.(plyrRef)
    }
  }, [onDestroy, onEnd, onPause, onPlay])

  return (
    <div className={cx('video-player', classNames)}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        preload="none"
        playsInline
        ref={playerRef}
        poster={poster || source?.poster}
        className={cx('video-player__content')}
      >
        {sources.length &&
          sources.map((sourceItem) => (
            <source
              key={sourceItem.src}
              src={sourceItem.src}
              type={sourceItem.type}
            />
          ))}
        {tracks.length &&
          tracks.map((track) => (
            <track
              key={track.src}
              kind={track.kind}
              label={track.label}
              srcLang={track.srcLang}
              src={track.src}
              default={track.default}
            />
          ))}
        Your browser doesn&apos;t support HTML5 video tag.
      </video>
      {isPlayButtonShown && (
        <button
          type="button"
          className={cx('video-player__play-button')}
          onClick={playVideo}
          aria-label="Play video"
        >
          {playButtonIcon}
        </button>
      )}
      {placeholder && (
        <div ref={placeholderRef} className={cx('placeholder')} />
      )}
    </div>
  )
}

export default VideoPlayer
